import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
    ArrowLeft,
    Save,
    Plus,
    Trash2,
    Video,
    BookOpen,
    Link as LinkIcon,
    CheckCircle,
    AlertCircle,
    Eye,
    FileText,
    ExternalLink,
    Loader2
} from 'lucide-react';
import resourceService from '../../services/resource.service';
import { extractVideoId, getThumbnailUrl, isValidVideoId } from '../../utils/youtubeUtils';
import toast from 'react-hot-toast';

const CATEGORIES = [
    'depression', 'anxiety', 'panic-attacks', 'stress-management',
    'substance-abuse', 'alcohol-abuse', 'drug-addiction', 'pornography-addiction',
    'technology-addiction', 'social-media-addiction', 'sleep-hygiene', 'insomnia',
    'relationships', 'breakups', 'domestic-violence', 'sexual-assault',
    'self-esteem', 'body-image', 'eating-disorders', 'academic-performance',
    'academic-pressure', 'exam-anxiety', 'procrastination', 'burnout',
    'grief-loss', 'suicide-ideation', 'suicide-prevention', 'self-harm',
    'trauma', 'ptsd', 'bipolar-disorder', 'adhd', 'ocd',
    'loneliness', 'social-isolation', 'identity-issues', 'gender-identity',
    'sexual-orientation', 'cultural-adjustment', 'racism-discrimination',
    'financial-stress', 'family-conflict', 'peer-pressure', 'bullying',
    'cyberbullying', 'anger-management', 'perfectionism', 'imposter-syndrome',
    'crisis-support', 'emergency-resources', 'coping-skills', 'mindfulness',
    'meditation', 'general-wellness', 'resilience', 'emotional-regulation'
];

const CONTENT_TYPES = ['article', 'video', 'tool', 'download', 'infographic'];
const DIFFICULTIES = ['beginner', 'intermediate', 'advanced'];
const EBOOK_FORMATS = ['PDF', 'EPUB', 'Online'];

const ResourceManagement = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isEditing = Boolean(slug);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        title: '',
        category: '',
        contentType: 'article',
        description: '',
        content: '',
        thumbnailUrl: '',
        mediaUrl: '',
        tags: '',
        readTime: '',
        difficulty: 'beginner',
        isPublished: false,
        isFeatured: false,
        youtubeVideos: [],
        ebooks: []
    });

    // YouTube video input state
    const [videoUrl, setVideoUrl] = useState('');
    const [videoTitle, setVideoTitle] = useState('');
    const [videoDescription, setVideoDescription] = useState('');
    const [videoDuration, setVideoDuration] = useState('');
    const [videoPreview, setVideoPreview] = useState(null);
    const [videoError, setVideoError] = useState('');

    // E-book input state
    const [ebookTitle, setEbookTitle] = useState('');
    const [ebookUrl, setEbookUrl] = useState('');
    const [ebookDescription, setEbookDescription] = useState('');
    const [ebookAuthor, setEbookAuthor] = useState('');
    const [ebookPages, setEbookPages] = useState('');
    const [ebookFormat, setEbookFormat] = useState('PDF');

    // Fetch resource if editing
    useEffect(() => {
        if (isEditing) {
            fetchResource();
        }
    }, [slug]);

    const fetchResource = async () => {
        try {
            setLoading(true);
            const response = await resourceService.getResourceBySlug(slug);
            const resource = response.data.resource;

            setFormData({
                title: resource.title || '',
                category: resource.category || '',
                contentType: resource.contentType || 'article',
                description: resource.description || '',
                content: resource.content || '',
                thumbnailUrl: resource.thumbnailUrl || '',
                mediaUrl: resource.mediaUrl || '',
                tags: resource.tags?.join(', ') || '',
                readTime: resource.readTime || '',
                difficulty: resource.difficulty || 'beginner',
                isPublished: resource.isPublished || false,
                isFeatured: resource.isFeatured || false,
                youtubeVideos: resource.youtubeVideos || [],
                ebooks: resource.ebooks || []
            });
        } catch (err) {
            setError('Failed to load resource');
            toast.error('Failed to load resource');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Handle YouTube URL input
    const handleVideoUrlChange = (e) => {
        const url = e.target.value;
        setVideoUrl(url);
        setVideoError('');
        setVideoPreview(null);

        if (url.trim()) {
            const videoId = extractVideoId(url);
            if (videoId && isValidVideoId(videoId)) {
                setVideoPreview({
                    videoId,
                    thumbnailUrl: getThumbnailUrl(videoId, 'mqdefault')
                });
            } else if (url.length > 10) {
                setVideoError('Invalid YouTube URL. Please paste a valid YouTube video link.');
            }
        }
    };

    const addYouTubeVideo = () => {
        if (!videoPreview) {
            setVideoError('Please enter a valid YouTube URL first');
            return;
        }
        if (!videoTitle.trim()) {
            setVideoError('Please enter a title for the video');
            return;
        }

        const newVideo = {
            videoId: videoPreview.videoId,
            title: videoTitle.trim(),
            description: videoDescription.trim(),
            duration: videoDuration.trim(),
            thumbnailUrl: videoPreview.thumbnailUrl
        };

        setFormData(prev => ({
            ...prev,
            youtubeVideos: [...prev.youtubeVideos, newVideo]
        }));

        // Reset video form
        setVideoUrl('');
        setVideoTitle('');
        setVideoDescription('');
        setVideoDuration('');
        setVideoPreview(null);
        setVideoError('');

        toast.success('Video added successfully');
    };

    const removeYouTubeVideo = (index) => {
        setFormData(prev => ({
            ...prev,
            youtubeVideos: prev.youtubeVideos.filter((_, i) => i !== index)
        }));
        toast.success('Video removed');
    };

    const addEbook = () => {
        if (!ebookTitle.trim()) {
            toast.error('Please enter an e-book title');
            return;
        }
        if (!ebookUrl.trim()) {
            toast.error('Please enter an e-book URL');
            return;
        }

        const newEbook = {
            title: ebookTitle.trim(),
            url: ebookUrl.trim(),
            description: ebookDescription.trim(),
            author: ebookAuthor.trim(),
            pages: ebookPages ? parseInt(ebookPages) : null,
            format: ebookFormat
        };

        setFormData(prev => ({
            ...prev,
            ebooks: [...prev.ebooks, newEbook]
        }));

        // Reset e-book form
        setEbookTitle('');
        setEbookUrl('');
        setEbookDescription('');
        setEbookAuthor('');
        setEbookPages('');
        setEbookFormat('PDF');

        toast.success('E-book added successfully');
    };

    const removeEbook = (index) => {
        setFormData(prev => ({
            ...prev,
            ebooks: prev.ebooks.filter((_, i) => i !== index)
        }));
        toast.success('E-book removed');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.title.trim()) {
            toast.error('Title is required');
            return;
        }
        if (!formData.category) {
            toast.error('Category is required');
            return;
        }
        if (!formData.description.trim()) {
            toast.error('Description is required');
            return;
        }

        try {
            setSaving(true);

            const submitData = {
                ...formData,
                tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
                readTime: formData.readTime ? parseInt(formData.readTime) : null
            };

            if (isEditing) {
                // Get resource ID first
                const currentResource = await resourceService.getResourceBySlug(slug);
                await resourceService.updateResource(currentResource.data.resource._id, submitData);
                toast.success('Resource updated successfully');
            } else {
                await resourceService.createResource(submitData);
                toast.success('Resource created successfully');
            }

            navigate(`/${user.role}/resources`);
        } catch (err) {
            toast.error(err.message || 'Failed to save resource');
        } finally {
            setSaving(false);
        }
    };

    const formatCategoryName = (category) => {
        return category
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
                    >
                        <ArrowLeft className="h-5 w-5" />
                        Back
                    </button>
                    <h1 className="text-3xl font-bold text-gray-900">
                        {isEditing ? 'Edit Resource' : 'Create New Resource'}
                    </h1>
                    <p className="text-gray-600 mt-2">
                        {isEditing
                            ? 'Update the resource information below'
                            : 'Fill in the details to create a new mental health resource'}
                    </p>
                </div>

                {error && (
                    <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg">
                        <div className="flex">
                            <AlertCircle className="h-5 w-5 text-red-400" />
                            <p className="ml-3 text-sm text-red-700">{error}</p>
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Basic Information */}
                    <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-6">Basic Information</h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Title *
                                </label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Enter resource title"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Category *
                                </label>
                                <select
                                    name="category"
                                    value={formData.category}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    required
                                >
                                    <option value="">Select a category</option>
                                    {CATEGORIES.map(cat => (
                                        <option key={cat} value={cat}>
                                            {formatCategoryName(cat)}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Content Type *
                                </label>
                                <select
                                    name="contentType"
                                    value={formData.contentType}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    {CONTENT_TYPES.map(type => (
                                        <option key={type} value={type}>
                                            {type.charAt(0).toUpperCase() + type.slice(1)}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Description *
                                </label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    rows="3"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Brief description of the resource"
                                    required
                                />
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Content (HTML/Markdown)
                                </label>
                                <textarea
                                    name="content"
                                    value={formData.content}
                                    onChange={handleInputChange}
                                    rows="10"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                                    placeholder="Enter the main content of the resource..."
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Thumbnail URL
                                </label>
                                <input
                                    type="url"
                                    name="thumbnailUrl"
                                    value={formData.thumbnailUrl}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="https://example.com/image.jpg"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Tags (comma separated)
                                </label>
                                <input
                                    type="text"
                                    name="tags"
                                    value={formData.tags}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="anxiety, coping, students"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Read Time (minutes)
                                </label>
                                <input
                                    type="number"
                                    name="readTime"
                                    value={formData.readTime}
                                    onChange={handleInputChange}
                                    min="1"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="10"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Difficulty
                                </label>
                                <select
                                    name="difficulty"
                                    value={formData.difficulty}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    {DIFFICULTIES.map(diff => (
                                        <option key={diff} value={diff}>
                                            {diff.charAt(0).toUpperCase() + diff.slice(1)}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="md:col-span-2 flex gap-6">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="isPublished"
                                        checked={formData.isPublished}
                                        onChange={handleInputChange}
                                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-sm font-medium text-gray-700">Published</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="isFeatured"
                                        checked={formData.isFeatured}
                                        onChange={handleInputChange}
                                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-sm font-medium text-gray-700">Featured</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* YouTube Videos Section */}
                    <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
                        <div className="flex items-center gap-2 mb-6">
                            <Video className="h-6 w-6 text-red-600" />
                            <h2 className="text-xl font-semibold text-gray-900">YouTube Videos</h2>
                        </div>

                        <p className="text-sm text-gray-600 mb-4">
                            Add YouTube videos related to this resource. Just paste the video URL and we'll extract the video automatically.
                        </p>

                        {/* Add Video Form */}
                        <div className="bg-gray-50 rounded-lg p-4 mb-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <LinkIcon className="inline h-4 w-4 mr-1" />
                                        YouTube URL
                                    </label>
                                    <input
                                        type="text"
                                        value={videoUrl}
                                        onChange={handleVideoUrlChange}
                                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                            videoError ? 'border-red-300' : 'border-gray-300'
                                        }`}
                                        placeholder="Paste YouTube URL (e.g., https://www.youtube.com/watch?v=... or https://youtu.be/...)"
                                    />
                                    {videoError && (
                                        <p className="text-sm text-red-600 mt-1">{videoError}</p>
                                    )}
                                </div>

                                {/* Video Preview */}
                                {videoPreview && (
                                    <div className="md:col-span-2 flex items-center gap-4 p-3 bg-white rounded-lg border border-green-200">
                                        <img
                                            src={videoPreview.thumbnailUrl}
                                            alt="Video preview"
                                            className="w-32 h-20 object-cover rounded"
                                        />
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 text-green-600">
                                                <CheckCircle className="h-4 w-4" />
                                                <span className="text-sm font-medium">Video detected!</span>
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">
                                                Video ID: {videoPreview.videoId}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Video Title *
                                    </label>
                                    <input
                                        type="text"
                                        value={videoTitle}
                                        onChange={(e) => setVideoTitle(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Enter a title for this video"
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Description (optional)
                                    </label>
                                    <textarea
                                        value={videoDescription}
                                        onChange={(e) => setVideoDescription(e.target.value)}
                                        rows="2"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Brief description of the video content"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Duration (optional)
                                    </label>
                                    <input
                                        type="text"
                                        value={videoDuration}
                                        onChange={(e) => setVideoDuration(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="e.g., 12:34"
                                    />
                                </div>

                                <div className="flex items-end">
                                    <button
                                        type="button"
                                        onClick={addYouTubeVideo}
                                        disabled={!videoPreview}
                                        className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                                    >
                                        <Plus className="h-4 w-4" />
                                        Add Video
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Added Videos List */}
                        {formData.youtubeVideos.length > 0 && (
                            <div className="space-y-3">
                                <h4 className="font-medium text-gray-700">Added Videos ({formData.youtubeVideos.length})</h4>
                                {formData.youtubeVideos.map((video, index) => (
                                    <div key={index} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                                        <img
                                            src={video.thumbnailUrl || `https://img.youtube.com/vi/${video.videoId}/mqdefault.jpg`}
                                            alt={video.title}
                                            className="w-24 h-16 object-cover rounded"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-gray-900 truncate">{video.title}</p>
                                            {video.duration && (
                                                <p className="text-sm text-gray-500">{video.duration}</p>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removeYouTubeVideo(index)}
                                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                            title="Remove video"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* E-books Section */}
                    <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
                        <div className="flex items-center gap-2 mb-6">
                            <BookOpen className="h-6 w-6 text-green-600" />
                            <h2 className="text-xl font-semibold text-gray-900">Free E-books & Resources</h2>
                        </div>

                        <p className="text-sm text-gray-600 mb-4">
                            Add links to free e-books, PDFs, and online resources that students can access.
                        </p>

                        {/* Add E-book Form */}
                        <div className="bg-gray-50 rounded-lg p-4 mb-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        E-book Title *
                                    </label>
                                    <input
                                        type="text"
                                        value={ebookTitle}
                                        onChange={(e) => setEbookTitle(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Enter e-book title"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Author (optional)
                                    </label>
                                    <input
                                        type="text"
                                        value={ebookAuthor}
                                        onChange={(e) => setEbookAuthor(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Author name"
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <LinkIcon className="inline h-4 w-4 mr-1" />
                                        E-book URL *
                                    </label>
                                    <input
                                        type="url"
                                        value={ebookUrl}
                                        onChange={(e) => setEbookUrl(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="https://example.org/free-ebook.pdf"
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Description (optional)
                                    </label>
                                    <textarea
                                        value={ebookDescription}
                                        onChange={(e) => setEbookDescription(e.target.value)}
                                        rows="2"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Brief description of the e-book"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Format
                                    </label>
                                    <select
                                        value={ebookFormat}
                                        onChange={(e) => setEbookFormat(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        {EBOOK_FORMATS.map(format => (
                                            <option key={format} value={format}>{format}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Pages (optional)
                                    </label>
                                    <input
                                        type="number"
                                        value={ebookPages}
                                        onChange={(e) => setEbookPages(e.target.value)}
                                        min="1"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Number of pages"
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <button
                                        type="button"
                                        onClick={addEbook}
                                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                                    >
                                        <Plus className="h-4 w-4" />
                                        Add E-book
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Added E-books List */}
                        {formData.ebooks.length > 0 && (
                            <div className="space-y-3">
                                <h4 className="font-medium text-gray-700">Added E-books ({formData.ebooks.length})</h4>
                                {formData.ebooks.map((ebook, index) => (
                                    <div key={index} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                                        <div className="flex-shrink-0 p-2 bg-green-100 rounded-lg">
                                            <FileText className="h-6 w-6 text-green-600" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-gray-900 truncate">{ebook.title}</p>
                                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                                {ebook.author && <span>By {ebook.author}</span>}
                                                <span className="px-2 py-0.5 bg-gray-200 rounded text-xs">{ebook.format}</span>
                                                {ebook.pages && <span>{ebook.pages} pages</span>}
                                            </div>
                                        </div>
                                        <a
                                            href={ebook.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                            title="Open link"
                                        >
                                            <ExternalLink className="h-4 w-4" />
                                        </a>
                                        <button
                                            type="button"
                                            onClick={() => removeEbook(index)}
                                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                            title="Remove e-book"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Submit Buttons */}
                    <div className="flex justify-end gap-4">
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors flex items-center gap-2"
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save className="h-4 w-4" />
                                    {isEditing ? 'Update Resource' : 'Create Resource'}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ResourceManagement;
